export interface VideoReferenceWorkflowSetting {
  brandId: string;
  brandName: string;
  /**
   * The Sim workflow is an optional secondary control. A null URL means the
   * brand is enabled through its configured prompt without a Sim fallback.
   */
  videoPromptVariantEditorSimUrl: string | null;
  /**
   * The API advertises this explicitly because clearing a prompt is only safe
   * when the brand can actually fall back to Sim.
   */
  canUseSimFallback: boolean;
  /** Null means prompt generation intentionally falls back to the legacy Sim workflow. */
  currentPromptVersionId: string | null;
  currentPromptVersionNumber: number | null;
  currentPromptVersion: {
    id: string;
    versionNumber: number;
  } | null;
  /** Active prompt versions by Video Reference generation type, when supplied by the API. */
  currentPromptVersions?: VideoReferencePromptVersionSummaryByType;
  updatedAt: string | null;
}

export const VIDEO_REFERENCE_PROMPT_TYPES = ["general", "ugc", "3d"] as const;
export type VideoReferencePromptType = (typeof VIDEO_REFERENCE_PROMPT_TYPES)[number];

export interface VideoReferencePromptVersionSummary {
  id: string;
  versionNumber: number;
}

export type VideoReferencePromptVersionSummaryByType = Record<
  VideoReferencePromptType,
  VideoReferencePromptVersionSummary | null
>;

export interface VideoReferencePromptVersion {
  id: string;
  brandId: string;
  versionNumber: number;
  promptText: string;
  createdBy: string | null;
  createdByName?: string | null;
  createdAt: string | null;
}

export interface VideoReferencePromptConfiguration {
  brandId: string;
  brandName: string;
  currentVersion: VideoReferencePromptVersion | null;
  versions: VideoReferencePromptVersion[];
  starterPrompt?: string;
  /** Whether clearing the active prompt can safely route new runs to Sim. */
  canUseSimFallback: boolean;
}

export interface VideoReferenceWorkflowResponse {
  enabled: VideoReferenceWorkflowSetting[];
  availableBrands: Array<{ brandId: string; brandName: string }>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isAvailableBrand(
  value: unknown,
): value is VideoReferenceWorkflowResponse["availableBrands"][number] {
  return isRecord(value)
    && typeof value.brandId === "string"
    && typeof value.brandName === "string";
}

function isEnabledSetting(value: unknown): value is VideoReferenceWorkflowSetting {
  return isRecord(value)
    && typeof value.brandId === "string"
    && typeof value.brandName === "string"
    && (value.videoPromptVariantEditorSimUrl === undefined
      || value.videoPromptVariantEditorSimUrl === null
      || typeof value.videoPromptVariantEditorSimUrl === "string")
    && (value.canUseSimFallback === undefined || typeof value.canUseSimFallback === "boolean")
    && (value.currentPromptVersionId === undefined
      || value.currentPromptVersionId === null
      || typeof value.currentPromptVersionId === "string")
    && (value.currentPromptVersionNumber === undefined
      || value.currentPromptVersionNumber === null
      || (typeof value.currentPromptVersionNumber === "number"
        && Number.isInteger(value.currentPromptVersionNumber)
        && value.currentPromptVersionNumber > 0))
    && (value.currentPromptVersion === undefined
      || value.currentPromptVersion === null
      || (isRecord(value.currentPromptVersion)
        && typeof value.currentPromptVersion.id === "string"
        && typeof value.currentPromptVersion.versionNumber === "number"
        && Number.isInteger(value.currentPromptVersion.versionNumber)
        && value.currentPromptVersion.versionNumber > 0))
    && (value.currentPromptVersions === undefined
      || isPromptVersionSummaryByType(value.currentPromptVersions))
    && (value.updatedAt === null || typeof value.updatedAt === "string");
}

function isPromptVersionSummary(value: unknown): value is VideoReferencePromptVersionSummary {
  return isRecord(value)
    && typeof value.id === "string"
    && typeof value.versionNumber === "number"
    && Number.isInteger(value.versionNumber)
    && value.versionNumber > 0;
}

function isPromptVersionSummaryByType(value: unknown): value is VideoReferencePromptVersionSummaryByType {
  return isRecord(value)
    && VIDEO_REFERENCE_PROMPT_TYPES.every((type) =>
      value[type] === null || isPromptVersionSummary(value[type]),
    );
}

function isPromptVersion(value: unknown): value is VideoReferencePromptVersion {
  return isRecord(value)
    && typeof value.id === "string"
    && typeof value.brandId === "string"
    && typeof value.versionNumber === "number"
    && Number.isInteger(value.versionNumber)
    && value.versionNumber > 0
    && typeof value.promptText === "string"
    && (value.createdBy === null || typeof value.createdBy === "string")
    && (value.createdByName === undefined
      || value.createdByName === null
      || typeof value.createdByName === "string")
    && (value.createdAt === null || typeof value.createdAt === "string");
}

export function normalizeVideoReferencePromptConfiguration(
  value: unknown,
): VideoReferencePromptConfiguration {
  const versions = isRecord(value) && Array.isArray(value.versions)
    ? value.versions
    : null;
  const currentVersionId = isRecord(value) && (
    value.currentPromptVersionId === null || typeof value.currentPromptVersionId === "string"
  )
    ? value.currentPromptVersionId
    : undefined;
  if (
    !isRecord(value)
    || (value.brandId !== undefined && typeof value.brandId !== "string")
    || (value.brandName !== undefined && typeof value.brandName !== "string")
    || (value.starterPrompt !== undefined && typeof value.starterPrompt !== "string")
    || (value.canUseSimFallback !== undefined && typeof value.canUseSimFallback !== "boolean")
    || !versions
    || !versions.every(isPromptVersion)
    || (value.currentVersion !== undefined
      && value.currentVersion !== null
      && !isPromptVersion(value.currentVersion))
    || (value.currentVersion === undefined && currentVersionId === undefined)
  ) {
    throw new Error("Prompt configuration returned an unusable response. Retry to load it again.");
  }
  const currentVersion = value.currentVersion === undefined
    ? versions.find((version) => version.id === currentVersionId) ?? null
    : value.currentVersion;
  return {
    brandId: typeof value.brandId === "string" ? value.brandId : versions[0]?.brandId ?? "",
    brandName: typeof value.brandName === "string" ? value.brandName : "",
    starterPrompt: typeof value.starterPrompt === "string" ? value.starterPrompt : undefined,
    canUseSimFallback: value.canUseSimFallback === true,
    currentVersion,
    versions: [...versions].sort((a, b) => b.versionNumber - a.versionNumber),
  };
}

export function normalizeVideoReferenceWorkflowResponse(
  value: unknown,
): VideoReferenceWorkflowResponse {
  if (
    !isRecord(value)
    || !Array.isArray(value.enabled)
    || !Array.isArray(value.availableBrands)
    || !value.enabled.every(isEnabledSetting)
    || !value.availableBrands.every(isAvailableBrand)
  ) {
    throw new Error("Workflow settings returned an unusable response. Retry to load them again.");
  }

  return {
    enabled: value.enabled.map((setting) => ({
      ...setting,
      videoPromptVariantEditorSimUrl: setting.videoPromptVariantEditorSimUrl ?? null,
      canUseSimFallback: setting.canUseSimFallback === true,
      currentPromptVersionId:
        setting.currentPromptVersionId ?? setting.currentPromptVersion?.id ?? null,
      currentPromptVersionNumber:
        setting.currentPromptVersionNumber ?? setting.currentPromptVersion?.versionNumber ?? null,
      currentPromptVersion: setting.currentPromptVersion
        ?? (setting.currentPromptVersionId && setting.currentPromptVersionNumber
          ? {
              id: setting.currentPromptVersionId,
              versionNumber: setting.currentPromptVersionNumber,
            }
          : null),
      currentPromptVersions: setting.currentPromptVersions
        ? { ...setting.currentPromptVersions }
        : {
            general: setting.currentPromptVersion
              ? {
                  id: setting.currentPromptVersion.id,
                  versionNumber: setting.currentPromptVersion.versionNumber,
                }
              : setting.currentPromptVersionId && setting.currentPromptVersionNumber
                ? {
                    id: setting.currentPromptVersionId,
                    versionNumber: setting.currentPromptVersionNumber,
                  }
                : null,
            ugc: null,
            "3d": null,
          },
    })),
    availableBrands: value.availableBrands,
  };
}